import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api.js';
import { validateSlug } from '../lib/slugify.js';
import { composeOrgEditBody } from '../lib/orgEdit.js';

import RelativeTime from '../components/shared/RelativeTime.jsx';
import { formatUtc } from '@vista-cloud/web-common';
const ROLE_LABELS = {
  org_admin: 'Admin',
  org_ro: 'Read Only',
  vista_admin: 'VISTA Admin',
  deactivated: 'Deactivated',
};

const ROLE_COLORS = {
  org_admin: 'bg-blue-100 text-blue-700',
  org_ro: 'bg-gray-100 text-gray-600',
  deactivated: 'bg-red-100 text-red-600',
};

const STATUS_COLORS = {
  active: 'bg-green-100 text-green-700',
  inactive: 'bg-gray-100 text-gray-500',
  unknown: 'bg-yellow-100 text-yellow-700',
};

const TABS = ['Overview', 'Users', 'Agents', 'Audit Log'];

function SummaryCard({ label, value, sub }) {
  return (
    <div className="rounded-lg border border-gray-200 bg-white px-4 py-3">
      <div className="text-xs font-medium text-gray-500">{label}</div>
      <div className="mt-1 text-2xl font-bold text-gray-900">{value}</div>
      {sub && <div className="mt-0.5 text-xs text-gray-400">{sub}</div>}
    </div>
  );
}

function EditOrgModal({ org, onClose, onSubmit, isPending, error }) {
  const [name, setName] = useState(org.name);
  const [slug, setSlug] = useState(org.slug);
  const [dedup, setDedup] = useState(Boolean(org.cross_agent_dedup_enabled));
  const [networkServices, setNetworkServices] = useState(Boolean(org.network_services_enabled));

  const slugError = validateSlug(slug);
  const nameChanged = name !== org.name;
  const slugChanged = slug !== org.slug;
  const dedupChanged = dedup !== Boolean(org.cross_agent_dedup_enabled);
  const networkServicesChanged = networkServices !== Boolean(org.network_services_enabled);
  const isNoop = !nameChanged && !slugChanged && !dedupChanged && !networkServicesChanged;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
        <h3 className="text-lg font-bold text-gray-900">Edit Organization</h3>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            onSubmit(composeOrgEditBody(org, { name, slug, dedup, networkServices }));
          }}
          className="mt-4 space-y-4"
        >
          <div>
            <label className="block text-sm font-medium text-gray-700">Org ID</label>
            <input
              type="text"
              value={org.org_id}
              readOnly
              disabled
              className="mt-1 w-full rounded-md border border-gray-200 bg-gray-50 px-3 py-1.5 font-mono text-sm text-gray-500"
            />
            <p className="mt-1 text-xs text-gray-500">Immutable — used in audit log and internal references.</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Organization Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              className="mt-1 w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Slug</label>
            <input
              type="text"
              value={slug}
              onChange={(e) => setSlug(e.target.value.toLowerCase())}
              required
              pattern="[a-z0-9]+(-[a-z0-9]+)*"
              className="mt-1 w-full rounded-md border border-gray-300 px-3 py-1.5 font-mono text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
            <p className="mt-1 text-xs text-gray-500">
              URL identifier — <code className="text-gray-700">{slug || 'slug'}.vista-cloud.io</code>
            </p>
            {slugError && <p className="mt-1 text-xs text-red-600">{slugError}</p>}
          </div>
          {slugChanged && (
            <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
              <strong>Warning:</strong> changing the slug breaks the live{' '}
              <code>{org.slug}.vista-cloud.io</code> URL. Users impersonating this org will
              need the new <code>{slug}.vista-cloud.io</code>; agents at{' '}
              <code>*.{org.slug}.vista-cloud.io</code> will need their tunnels re-pointed.
              The org_id stays <code>{org.org_id}</code>.
            </div>
          )}
          <div className="border-t border-gray-100 pt-4">
            <label className="flex items-start gap-3">
              <input
                type="checkbox"
                checked={dedup}
                onChange={(e) => setDedup(e.target.checked)}
                className="mt-0.5 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="text-sm">
                <span className="font-medium text-gray-700">Cross-agent device dedup</span>
                <span className="block text-xs text-gray-500">
                  Federates the device list across all agents in this org so devices visible
                  to more than one agent collapse into a single row. Enable when multiple
                  agents observe the same physical network — HA pairs (primary / standby or
                  load-shared) or deployments with multiple access networks under one
                  logical network. Leave off when each agent monitors a distinct network
                  with no device overlap (the default).
                </span>
              </span>
            </label>
          </div>
          <div className="border-t border-gray-100 pt-4">
            <label className="flex items-start gap-3">
              <input
                type="checkbox"
                checked={networkServices}
                onChange={(e) => setNetworkServices(e.target.checked)}
                className="mt-0.5 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="text-sm">
                <span className="font-medium text-gray-700">Network Services</span>
                <span className="block text-xs text-gray-500">
                  Surfaces the top-level <strong>Services</strong> view — health of
                  infrastructure services VISTA polls over a management API (Bind9 DNS,
                  Kea DHCP). Enable for orgs running those services; the operator then
                  configures the endpoints on the agent. Leave off (the default) and the
                  Services nav item stays hidden.
                </span>
              </span>
            </label>
          </div>
          {error && <p className="text-sm text-red-600">{error}</p>}
          <div className="flex justify-end gap-3">
            <button type="button" onClick={onClose} className="rounded-md border border-gray-300 px-4 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50">
              Cancel
            </button>
            <button
              type="submit"
              disabled={isPending || isNoop || Boolean(slugError)}
              className="rounded-md bg-blue-600 px-4 py-1.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
            >
              {isPending ? 'Saving...' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function OverviewTab({ org }) {
  const agents = org.agents || [];
  const activeAgents = agents.filter((a) => a.status === 'active');

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <SummaryCard label="Agents" value={agents.length} sub={`${activeAgents.length} connected`} />
        <SummaryCard label="Users" value={org.user_count ?? 0} />
        {/* §3.11 carve-out: a creation DATE is a wall-clock fact, not a
            freshness display, so it stays absolute — but the guideline
            requires the UTC suffix even there. */}
        <SummaryCard label="Created" value={formatUtc(org.created_at).slice(0, 10) + " UTC"} />
        <SummaryCard label="Org ID" value={org.org_id} />
      </div>

      {agents.length > 0 && (
        <div className="rounded-lg border border-gray-200 bg-white">
          <div className="border-b border-gray-200 px-4 py-3">
            <h3 className="text-sm font-medium text-gray-900">Agent Status</h3>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left text-xs text-gray-500">
              <tr>
                <th className="px-4 py-2">Name</th>
                <th className="px-4 py-2">Endpoint</th>
                <th className="px-4 py-2">Last Seen</th>
                <th className="px-4 py-2">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {agents.map((a) => (
                <tr key={a.agent_id}>
                  <td className="px-4 py-2 font-medium">{a.name}</td>
                  <td className="px-4 py-2 font-mono text-xs text-gray-500">{a.endpoint_url}</td>
                  <td className="px-4 py-2 text-gray-500"><RelativeTime ts={a.last_seen} fallback="Never" /></td>
                  <td className="px-4 py-2">
                    <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_COLORS[a.status] || STATUS_COLORS.unknown}`}>
                      {a.status || 'unknown'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function UsersTab({ org, orgId, queryClient }) {
  const users = org.users || [];
  const pendingInvitations = org.pending_invitations || [];
  const [showInvite, setShowInvite] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('org_ro');
  const [deactivatingUser, setDeactivatingUser] = useState(null);

  // Invite uses the org dashboard's invitation API via the admin workers
  // (admin workers share the same D1, so we write directly)
  const inviteMutation = useMutation({
    mutationFn: (data) => api.post(`/vista-admin/organizations/${orgId}/invite`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['org-detail', orgId] });
      setShowInvite(false);
      setInviteEmail('');
      setInviteRole('org_ro');
    },
  });

  const deactivateMutation = useMutation({
    mutationFn: (userId) => api.delete(`/vista-admin/users/${userId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['org-detail', orgId] });
      setDeactivatingUser(null);
    },
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium text-gray-900">Organization Users</h3>
        {/* vista#336 — was "Invite User" + "Send Invite" but no email is
            actually sent. Honest labels: this pre-authorizes the email
            address; the user provisions themselves on first CF Access
            login (handleAcceptInvite auto-claims pending invitations
            with the matching email). Email-send wiring is a separate
            sprint item once an SMTP / Mailchannels integration lands. */}
        <button
          onClick={() => setShowInvite(!showInvite)}
          className="rounded-md bg-blue-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-blue-700"
        >
          Authorize User
        </button>
      </div>

      {showInvite && (
        <div className="rounded-lg border border-gray-200 bg-white p-4">
          <div className="mb-3 rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-xs text-blue-900">
            <strong>No email is sent.</strong> Share the org dashboard URL with the user
            out-of-band — on their first sign-in via Cloudflare Access, their email is
            matched against this entry and they're auto-provisioned with the role below.
            The authorization expires in 7 days if unclaimed.
          </div>
          <form
            onSubmit={(e) => { e.preventDefault(); inviteMutation.mutate({ email: inviteEmail, role: inviteRole }); }}
            className="flex items-end gap-3"
          >
            <div className="flex-1">
              <label className="block text-xs text-gray-500">Email</label>
              <input type="email" value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)} required
                className="mt-1 w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                placeholder="user@example.com" />
            </div>
            <div>
              <label className="block text-xs text-gray-500">Role</label>
              <select value={inviteRole} onChange={(e) => setInviteRole(e.target.value)}
                className="mt-1 rounded-md border border-gray-300 px-3 py-1.5 text-sm">
                <option value="org_admin">Admin</option>
                <option value="org_ro">Read Only</option>
              </select>
            </div>
            <button type="submit" disabled={inviteMutation.isPending}
              className="rounded-md bg-blue-600 px-4 py-1.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50">
              {inviteMutation.isPending ? 'Authorizing...' : 'Authorize'}
            </button>
          </form>
          {inviteMutation.isError && (
            <p className="mt-2 text-sm text-red-600">{inviteMutation.error?.body?.error || inviteMutation.error?.message}</p>
          )}
        </div>
      )}

      {deactivatingUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="w-full max-w-sm rounded-lg bg-white p-6 shadow-xl">
            <h3 className="text-lg font-bold text-gray-900">Deactivate User</h3>
            <p className="mt-2 text-sm text-gray-600">
              Deactivate <span className="font-semibold">{deactivatingUser.name}</span>? They will lose all access immediately.
            </p>
            <div className="mt-4 flex justify-end gap-3">
              <button onClick={() => setDeactivatingUser(null)} className="rounded-md border border-gray-300 px-4 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50">Cancel</button>
              <button onClick={() => deactivateMutation.mutate(deactivatingUser.user_id)} disabled={deactivateMutation.isPending}
                className="rounded-md bg-red-600 px-4 py-1.5 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50">
                {deactivateMutation.isPending ? 'Deactivating...' : 'Deactivate'}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="rounded-lg border border-gray-200 bg-white">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-left text-xs text-gray-500">
            <tr>
              <th className="px-4 py-2">Name</th>
              <th className="px-4 py-2">Email</th>
              <th className="px-4 py-2">Role</th>
              <th className="px-4 py-2">Last Login</th>
              <th className="px-4 py-2" />
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {users.length === 0 ? (
              <tr><td colSpan={5} className="px-4 py-8 text-center text-sm text-gray-400">No users in this organization</td></tr>
            ) : users.map((u) => (
              <tr key={u.user_id}>
                <td className="px-4 py-2 font-medium">{u.name}</td>
                <td className="px-4 py-2 text-gray-500">{u.email}</td>
                <td className="px-4 py-2">
                  <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${ROLE_COLORS[u.role] || 'bg-gray-100 text-gray-500'}`}>
                    {ROLE_LABELS[u.role] || u.role}
                  </span>
                </td>
                <td className="px-4 py-2 text-gray-500"><RelativeTime ts={u.last_login} fallback="Never" /></td>
                <td className="px-4 py-2 text-right">
                  {u.role !== 'deactivated' && (
                    <button onClick={() => setDeactivatingUser(u)}
                      className="rounded-md border border-red-300 px-2.5 py-1 text-xs font-medium text-red-600 hover:bg-red-50">
                      Deactivate
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* vista#695 — pending "Authorize User" invitations. These aren't full
          users yet (they self-provision on first Cloudflare Access login),
          so they'd otherwise be invisible in the table above, making the
          Authorize button look like it did nothing. */}
      {pendingInvitations.length > 0 && (
        <div className="rounded-lg border border-gray-200 bg-white">
          <div className="border-b border-gray-200 px-4 py-3">
            <h3 className="text-sm font-medium text-gray-900">Pending Authorizations</h3>
            <p className="mt-0.5 text-xs text-gray-500">
              These email addresses are pre-authorized. Each becomes a full user after
              their first sign-in via Cloudflare Access.
            </p>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left text-xs text-gray-500">
              <tr>
                <th className="px-4 py-2">Email</th>
                <th className="px-4 py-2">Role</th>
                <th className="px-4 py-2">Status</th>
                <th className="px-4 py-2">Expires</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {pendingInvitations.map((inv) => (
                <tr key={inv.email}>
                  <td className="px-4 py-2 text-gray-700">{inv.email}</td>
                  <td className="px-4 py-2">
                    <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${ROLE_COLORS[inv.role] || 'bg-gray-100 text-gray-500'}`}>
                      {ROLE_LABELS[inv.role] || inv.role}
                    </span>
                  </td>
                  <td className="px-4 py-2">
                    <span className="inline-flex rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700">
                      Pending
                    </span>
                  </td>
                  <td className="px-4 py-2 text-gray-500"><RelativeTime ts={inv.expires_at} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function AgentsTab({ org }) {
  const agents = org.agents || [];

  return (
    <div className="rounded-lg border border-gray-200 bg-white">
      <table className="w-full text-sm">
        <thead className="bg-gray-50 text-left text-xs text-gray-500">
          <tr>
            <th className="px-4 py-2">Name</th>
            <th className="px-4 py-2">Endpoint</th>
            <th className="px-4 py-2">Status</th>
            <th className="px-4 py-2">Last Seen</th>
            <th className="px-4 py-2">Created</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {agents.length === 0 ? (
            <tr><td colSpan={5} className="px-4 py-8 text-center text-sm text-gray-400">No agents registered</td></tr>
          ) : agents.map((a) => (
            <tr key={a.agent_id}>
              <td className="px-4 py-2 font-medium">{a.name}</td>
              <td className="px-4 py-2 font-mono text-xs text-gray-500">{a.endpoint_url}</td>
              <td className="px-4 py-2">
                <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_COLORS[a.status] || STATUS_COLORS.unknown}`}>
                  {a.status || 'unknown'}
                </span>
              </td>
              <td className="px-4 py-2 text-gray-500"><RelativeTime ts={a.last_seen} fallback="Never" /></td>
              <td className="px-4 py-2 text-gray-500"><RelativeTime ts={a.created_at} /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function AuditLogTab({ org }) {
  const allEntries = org.audit_log || [];
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const entries = allEntries.filter((e) => {
    if (startDate && e.created_at < startDate) return false;
    if (endDate && e.created_at > endDate + 'T23:59:59') return false;
    return true;
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-4">
        <div>
          <label className="block text-xs text-gray-500">From</label>
          <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)}
            className="mt-1 rounded-md border border-gray-300 px-3 py-1.5 text-sm" />
        </div>
        <div>
          <label className="block text-xs text-gray-500">To</label>
          <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)}
            className="mt-1 rounded-md border border-gray-300 px-3 py-1.5 text-sm" />
        </div>
        {(startDate || endDate) && (
          <button onClick={() => { setStartDate(''); setEndDate(''); }}
            className="mt-5 text-xs text-gray-400 hover:text-gray-600">Clear</button>
        )}
        <span className="mt-5 text-xs text-gray-400">{entries.length} entries</span>
      </div>

      <div className="rounded-lg border border-gray-200 bg-white">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-left text-xs text-gray-500">
            <tr>
              <th className="px-4 py-2">Timestamp</th>
              <th className="px-4 py-2">Actor</th>
              <th className="px-4 py-2">Action</th>
              <th className="px-4 py-2">Target</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {entries.length === 0 ? (
              <tr><td colSpan={4} className="px-4 py-8 text-center text-sm text-gray-400">No audit log entries</td></tr>
            ) : entries.map((e) => (
              <tr key={e.id}>
                <td className="px-4 py-2 text-gray-500 whitespace-nowrap"><RelativeTime ts={e.created_at} /></td>
                <td className="px-4 py-2">{e.actor_name || e.actor_email || e.user_id}</td>
                <td className="px-4 py-2 font-mono text-xs">{e.action}</td>
                <td className="px-4 py-2 text-gray-500">{e.resource_type}{e.resource_id ? `: ${e.resource_id}` : ''}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function OrgDetail() {
  const { orgId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [tab, setTab] = useState('Overview');
  const [showEdit, setShowEdit] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['org-detail', orgId],
    queryFn: () => api.get(`/vista-admin/organizations/${orgId}`),
  });

  const handoffMutation = useMutation({
    mutationFn: () => api.post('/vista-admin/handoff', { org_id: orgId }),
    onSuccess: (result) => {
      const d = result.data;
      // When the server returns a localhost URL but we're accessing from a
      // remote browser, replace the hostname so the handoff link is reachable.
      const handoff = new URL(d.url);
      if (handoff.hostname === 'localhost' && window.location.hostname !== 'localhost') {
        handoff.hostname = window.location.hostname;
      }
      window.open(handoff.toString(), '_blank');
    },
  });

  // vista#335 — PUT /organizations/:id supports partial name/slug update.
  // Invalidate both the detail query and the list query so the org list
  // page picks up the new values without a manual refresh.
  const editMutation = useMutation({
    mutationFn: (body) => api.put(`/vista-admin/organizations/${orgId}`, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['org-detail', orgId] });
      queryClient.invalidateQueries({ queryKey: ['organizations'] });
      setShowEdit(false);
    },
  });

  // vista#374 — (re)provision the org's CF Access app from the detail page.
  const provisionAccessMutation = useMutation({
    mutationFn: () => api.post(`/vista-admin/organizations/${orgId}/provision-access`, {}),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['org-detail', orgId] }),
  });

  const org = data?.data;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="mx-auto h-8 w-8 animate-spin rounded-full border-4 border-blue-600 border-t-transparent" />
      </div>
    );
  }

  if (!org || !org.org_id) {
    return (
      <div className="rounded-lg bg-white p-8 text-center shadow">
        <h1 className="text-xl font-bold text-gray-900">Organization Not Found</h1>
        <button onClick={() => navigate('/')} className="mt-4 text-sm text-blue-600 hover:text-blue-800">&larr; Back to Organizations</button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <button onClick={() => navigate('/')} className="text-sm text-gray-400 hover:text-gray-600">&larr; Organizations</button>
          <h1 className="mt-1 text-2xl font-bold text-gray-900">{org.name}</h1>
          <p className="text-sm text-gray-500">{org.slug}</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowEdit(true)}
            className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            Edit
          </button>
          <button
            onClick={() => handoffMutation.mutate()}
            disabled={handoffMutation.isPending}
            className="rounded-md border border-blue-300 px-4 py-2 text-sm font-medium text-blue-600 hover:bg-blue-50 disabled:opacity-50"
          >
            {handoffMutation.isPending ? 'Opening...' : 'Open Org View \u2192'}
          </button>
        </div>
      </div>

      {/* vista#374 — CF Access app status + provision button */}
      <div className="rounded-lg bg-white p-4 shadow">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-sm font-semibold text-gray-900">Cloudflare Access</h2>
            {org.cf_access_app_id ? (
              <p className="mt-1 text-xs text-green-700">
                Provisioned — app <code className="font-mono">{org.cf_access_app_id}</code> gates{' '}
                <code>{org.slug}.vista-cloud.io</code>.
              </p>
            ) : (
              <p className="mt-1 text-xs text-amber-700">
                Not provisioned — operators may hit a CF Access login failure on{' '}
                <code>{org.slug}.vista-cloud.io</code> until an Access app covers it.
              </p>
            )}
            {provisionAccessMutation.isError && (
              <p className="mt-1 text-xs text-red-600">
                {provisionAccessMutation.error?.body?.error || provisionAccessMutation.error?.message || 'Provision failed'}
              </p>
            )}
          </div>
          {!org.cf_access_app_id && (
            <button
              onClick={() => provisionAccessMutation.mutate()}
              disabled={provisionAccessMutation.isPending}
              className="shrink-0 rounded-md border border-blue-300 px-3 py-1.5 text-sm font-medium text-blue-600 hover:bg-blue-50 disabled:opacity-50"
            >
              {provisionAccessMutation.isPending ? 'Provisioning…' : 'Provision Access App'}
            </button>
          )}
        </div>
      </div>

      {showEdit && (
        <EditOrgModal
          org={org}
          onClose={() => { setShowEdit(false); editMutation.reset(); }}
          onSubmit={(body) => editMutation.mutate(body)}
          isPending={editMutation.isPending}
          error={editMutation.isError ? (editMutation.error?.body?.error || editMutation.error?.message) : null}
        />
      )}

      {handoffMutation.isError && (
        <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-600">
          {handoffMutation.error?.body?.error || handoffMutation.error?.message || 'Failed to open org view'}
        </div>
      )}

      <div className="border-b border-gray-200">
        <nav className="flex gap-6">
          {TABS.map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`border-b-2 pb-2 text-sm font-medium transition-colors ${
                tab === t
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {t}
            </button>
          ))}
        </nav>
      </div>

      {tab === 'Overview' && <OverviewTab org={org} />}
      {tab === 'Users' && <UsersTab org={org} orgId={orgId} queryClient={queryClient} />}
      {tab === 'Agents' && <AgentsTab org={org} />}
      {tab === 'Audit Log' && <AuditLogTab org={org} />}
    </div>
  );
}
